// @ts-nocheck

import BookingPassengerForm from '../BookingPassengerForm/BookingPassengerForm';
import { NUMBER } from '../../../../constants/passengers';
import { useState, useEffect } from 'react';
import { updatePassengerField } from '../../../../redux/slices/booking/booking';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { getCountries } from '../../../../services/country/country';
import * as PropTypes from 'prop-types';

/**
 * Functional React component for rendering a custom booking passengers component .
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the booking passengers component.
 *
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the booking passengers component.
 */

const BookingPassengerPanel = ({
  id,
  passengerFieldFiles = {},
  onPassengerFieldFilesChange,
  ...rest
}) => {
  const dispatch = useDispatch();
  const booking = useSelector((state) => state.booking);
  const [countries, setCountries] = useState([]);
  const passengerForms = booking?.passengerForms || [];
  const shouldGetCountries = passengerForms.length
    ? passengerForms[0]?.fields?.find(
        (field) =>
          field?.field === 'country' || field?.field === 'passportnationality'
      )
    : false;

  useEffect(() => {
    let isMounted = true;

    const fetchCountries = async () => {
      try {
        const response = await getCountries();
        const countriesList = response?.data || [];

        if (response && response?.data?.length) {
          const mappedCountries = countriesList.map((country) => ({
            ...country,
            title: country?.countryname
          }));

          if (isMounted) {
            setCountries(mappedCountries);
          }
        }
      } catch (error) {
        setCountries([]);
      }
    };

    if (shouldGetCountries) {
      fetchCountries();
    }

    return () => {
      isMounted = false;
    };
  }, [shouldGetCountries]);

  const handleOnChange = (formId, fieldId, newValue, type) => {
    if ((type === NUMBER && !isNaN(newValue?.trim())) || type !== NUMBER) {
      dispatch(
        updatePassengerField({
          formId,
          fieldId,
          newValue,
          error: null
        })
      );
    }
  };

  const handleOnFileChange = (formId, field, files = []) => {
    onPassengerFieldFilesChange?.(formId, field?.trippassengerfieldid, files);

    dispatch(
      updatePassengerField({
        formId,
        fieldId: field?.fieldId,
        newValue: files.map((file) => file?.name).join(', '),
        error: null
      })
    );
  };

  const handleOnFileError = (formId, field, error = '') => {
    if (!error) {
      return;
    }

    dispatch(
      updatePassengerField({
        formId,
        fieldId: field?.fieldId,
        newValue: field?.value ?? '',
        error: error || null
      })
    );
  };

  return (
    <Box
      id={id}
      data-testid='booking-passengers-form'
      {...rest}
      className='book-now-modal__payment-options book-now-modal___left-panel'
    >
      <div className='book-now-modal__plans-container'>
        {passengerForms &&
          passengerForms.map((form, index) => {
            const { formId } = form;

            return (
              <BookingPassengerForm
                key={formId}
                id={`booking-passenger-form-${formId}`}
                form={form}
                handleOnChange={handleOnChange}
                handleOnFileChange={handleOnFileChange}
                handleOnFileError={handleOnFileError}
                passengerFieldFiles={passengerFieldFiles}
                sequence={index + 1}
                countries={countries}
              />
            );
          })}
      </div>
    </Box>
  );
};

BookingPassengerPanel.propTypes = {
  id: PropTypes.string.isRequired,
  passengerFieldFiles: PropTypes.object,
  onPassengerFieldFilesChange: PropTypes.func
};
export default BookingPassengerPanel;
